/**
 * @file src/data/sosNumbers.ts
 * @description Números de emergencia oficiales por país para módulo SOS global
 * CRÍTICO: Estos números pueden salvar vidas - verificar antes de cualquier cambio
 */

export interface SOSService {
  numero: string;
  nombre: string;
  descripcion: string;
  icono: 'Stethoscope' | 'Flame' | 'ShieldAlert' | 'Phone' | 'Heart' | 'Shield';
  tel: string; // Formato para enlaces tel:
}

export interface SOSCountry {
  id: string; // Código ISO-2
  nombre: string;
  alias: string; // Para analytics
  botonesPrincipales: SOSService[];
  ayudas: SOSService[];
  notas?: string[];
}

/**
 * NÚMEROS OFICIALES DE EMERGENCIA POR PAÍS
 * Fuente: Organismos oficiales de cada país
 * Última actualización: 2025-08-16
 */
export const sosNumbers: Record<string, SOSCountry> = {
  CL: {
    id: 'CL',
    nombre: 'Chile',
    alias: 'chile',
    botonesPrincipales: [
      {
        numero: '131',
        nombre: 'SAMU',
        descripcion: 'Ambulancia - 24/7',
        icono: 'Stethoscope',
        tel: 'tel:131'
      },
      {
        numero: '132',
        nombre: 'Bomberos',
        descripcion: 'Emergencias de fuego - 24/7',
        icono: 'Flame',
        tel: 'tel:132'
      },
      {
        numero: '133',
        nombre: 'Carabineros',
        descripcion: 'Seguridad y orden - 24/7',
        icono: 'ShieldAlert',
        tel: 'tel:133'
      }
    ],
    ayudas: [
      {
        numero: '134',
        nombre: 'PDI',
        descripcion: 'Policía de Investigaciones',
        icono: 'Shield',
        tel: 'tel:134'
      },
      {
        numero: '136',
        nombre: 'Socorro Andino',
        descripcion: 'Rescate en montaña',
        icono: 'Shield',
        tel: 'tel:136'
      },
      {
        numero: '137',
        nombre: 'Salvamento marítimo',
        descripcion: 'Emergencias en el mar',
        icono: 'Shield',
        tel: 'tel:137'
      },
      {
        numero: '138',
        nombre: 'Búsqueda y Salvamento Aéreo',
        descripcion: 'Rescate aéreo',
        icono: 'Shield',
        tel: 'tel:138'
      },
      {
        numero: '1455',
        nombre: 'Violencias de género',
        descripcion: 'SernamEG - 24/7',
        icono: 'Heart',
        tel: 'tel:1455'
      },
      {
        numero: '149',
        nombre: 'Fono Familia',
        descripcion: 'Carabineros - Orientación familiar',
        icono: 'Phone',
        tel: 'tel:149'
      },
      {
        numero: '600 360 7777',
        nombre: 'Salud Responde',
        descripcion: 'MINSAL - Consultas médicas',
        icono: 'Stethoscope',
        tel: 'tel:+566003607777'
      },
      {
        numero: '*4141',
        nombre: 'Apoyo salud mental',
        descripcion: 'MINSAL - Salud mental',
        icono: 'Heart',
        tel: 'tel:*4141'
      }
    ],
    notas: [
      'Si estás en riesgo vital, llama a 131 inmediatamente.',
      'El 112 puede funcionar en algunos móviles, úsalo solo como alternativa.'
    ]
  },

  MX: {
    id: 'MX',
    nombre: 'México',
    alias: 'mexico',
    botonesPrincipales: [
      {
        numero: '911',
        nombre: 'Emergencias',
        descripcion: 'Línea única de emergencias - 24/7',
        icono: 'ShieldAlert',
        tel: 'tel:911'
      }
    ],
    ayudas: [
      {
        numero: '078',
        nombre: 'Ángeles Verdes',
        descripcion: 'Auxilio en carreteras',
        icono: 'Shield',
        tel: 'tel:078'
      }
    ],
    notas: [
      'El 911 conecta con todos los servicios de emergencia.',
      'Disponible desde cualquier teléfono fijo o móvil.'
    ]
  },

  AR: {
    id: 'AR',
    nombre: 'Argentina',
    alias: 'argentina',
    botonesPrincipales: [
      {
        numero: '911',
        nombre: 'Emergencias',
        descripcion: 'Línea única de emergencias - 24/7',
        icono: 'ShieldAlert',
        tel: 'tel:911'
      },
      {
        numero: '107',
        nombre: 'SAME',
        descripcion: 'Ambulancia - 24/7',
        icono: 'Stethoscope',
        tel: 'tel:107'
      },
      {
        numero: '100',
        nombre: 'Bomberos',
        descripcion: 'Emergencias de fuego - 24/7',
        icono: 'Flame',
        tel: 'tel:100'
      }
    ],
    ayudas: [],
    notas: [
      'El 911 es la línea principal para todas las emergencias.',
      'Los números específicos (107, 100) también están disponibles.'
    ]
  },

  CO: {
    id: 'CO',
    nombre: 'Colombia',
    alias: 'colombia',
    botonesPrincipales: [
      {
        numero: '123',
        nombre: 'Línea única de emergencias',
        descripcion: 'Todas las emergencias - 24/7',
        icono: 'ShieldAlert',
        tel: 'tel:123'
      }
    ],
    ayudas: [],
    notas: [
      'El 123 conecta con todos los servicios de emergencia.',
      'Disponible desde cualquier teléfono en Colombia.'
    ]
  },

  PE: {
    id: 'PE',
    nombre: 'Perú',
    alias: 'peru',
    botonesPrincipales: [
      {
        numero: '105',
        nombre: 'Policía',
        descripcion: 'Policía Nacional - 24/7',
        icono: 'ShieldAlert',
        tel: 'tel:105'
      },
      {
        numero: '116',
        nombre: 'Bomberos',
        descripcion: 'Emergencias de fuego - 24/7',
        icono: 'Flame',
        tel: 'tel:116'
      },
      {
        numero: '106',
        nombre: 'SAMU',
        descripcion: 'Ambulancia - 24/7',
        icono: 'Stethoscope',
        tel: 'tel:106'
      }
    ],
    ayudas: [],
    notas: [
      'Usa el número específico según el tipo de emergencia.',
      'Todos los servicios están disponibles 24/7.'
    ]
  }
};

/**
 * País por defecto (Chile)
 */
export const DEFAULT_COUNTRY = 'CL';

/**
 * Obtener datos de un país específico
 */
export const getCountryData = (countryId: string): SOSCountry | null => {
  return sosNumbers[countryId] || null;
};

/**
 * Obtener lista de países disponibles
 */
export const getAvailableCountries = (): SOSCountry[] => {
  return Object.values(sosNumbers);
};

/**
 * Obtener botones principales de Chile (para barra sticky)
 */
export const getChilePrimaryButtons = (): SOSService[] => {
  return sosNumbers.CL.botonesPrincipales;
};
